import { CommonModule } from "@angular/common";
import { Component, computed, signal } from "@angular/core";
import { ColDef } from "ag-grid-community";

// EZ
import { EzBaseComponent } from "@template-ui/components";
import { DocMeta } from "@template-ui/decorators";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { createTrigger } from "@template-ui/helpers";
import {
  ButtonConfig,
  CrudApiProtocol,
  CrudMatchType,
  EzGridContextMenuConfigFactory,
  EzFormInputType,
  EzGridColTypes,
  EzGridDsConfig,
  EzGridLoadMode,
  InputConfig,
  InputConfigForFilters,
  PanelHeaderConfig,
} from "@template-ui/types";
import { MarkdownModule } from "ngx-markdown";
import { EzButtonCellParams } from "@template-ui/components/ez-grid/cell-renderers/buttons-cell-renderer/buttons-cell-renderer.component";

type GridSampleScenario = "client-rowData" | "server-endpoint" | "server-ds-get" | "server-ds-odata";

type ModeFormValues = {
  scenario: GridSampleScenario;
  loadMode: EzGridLoadMode;
};

type StarWarsActor = {
  id: string;
  firstName: string;
  lastName: string;
  actorId: string;
  dateOfBirth: string;
  notableRole: string;
  trilogy: string;
  isLiving: boolean;
};

@DocMeta({
  hide: true,
  category: "Components",
  showcaseTitle: "Client/Server + Load Mode",
  showcaseDesc: `## This sample demonstrates how to get data into the EZ Grid and the loading options
  ### Getting data into the grid

  | Mode | Protocol | How |
  | --- |--- | --- |
  | Client | None | Manually set [rowData] |
  | Server (legacy, endpoint only) | Default | Just set [readEndpoint], EZ Grid will use GET protocol |
  | Server (legacy) | GET/POST_FORM/POST_JSON | Configure dsConfig, specify READ binding endpoint and protocol |
  | Server (OData)  | GET_ODATA | Configure dsConfig, specify READ binding endpoint and protocol to GET_ODATA |

  **Note:** For a **\`POST_X\` \`READ\`** endpoint, EZ Grid automatically includes paging, sorting, and filtering in the request body instead of query params<br/>
  (as with **\`GET\`**). Use **\`dsConfig.staticFilters\`** for always-on filter criteria. If necessary, use the **\`[postObj]\`** input for additional non-filter payload fields (rare).

  ### OData collection filters

  Use **\`odataTreatAsCollection\`** only when the OData field is a collection instead of a scalar field.
  This changes equality-style filters from **\`field eq value\`** to **\`field/any(...)\`** membership filters.

  If each collection item is an object, also set **\`odataCollectionItemField\`** to the property that should be compared.
  Example shape: **\`filterField: 'tags', odataTreatAsCollection: true, odataCollectionItemField: 'id'\`**

  Leave **\`odataCollectionItemField\`** unset for primitive collections such as **\`string[]\`** or **\`number[]\`**.

  ### Loading options

  | Load Mode | When data loads |
  | --- | --- |
  | auto | Loads immediately when the grid initializes and reloads automatically when filters/sort/page change. (default) |
  | defer-initial | Does not load on first render; then behaves like auto after the initial defer is consumed. |
  | require-filter | Loads only when at least one filter is applied. In this mode, instead of the \`noDataFoundMessage\`, the input \`filterRequiredMessage\` with a default value of "Data will be retrieved once a filter has been applied." will be shown.  |

  **Note:** **\`loadMode\`** applies to server-backed loading paths (**\`readEndpoint\`** / **\`dsConfig\`**).<br/>
  In client **\`[rowData]\`** mode, data renders when you set **\`rowData\`**. In this mode **\`loadMode\`** is ignored.
`,
})
@Component({
  selector: "ui-grid-load-data-sample, ez-grid-load-data-sample",
  standalone: true,
  imports: [CommonModule, UiLibraryModule, MarkdownModule],
  templateUrl: "./ez-grid-load-data-sample.component.html",
  styleUrl: "./ez-grid-load-data-sample.component.scss",
})
export class EzGridLoadDataSampleComponent extends EzBaseComponent {
  constructor() {
    super();
    this.thisClassName = 'EzGridLoadDataSampleComponent';
  }

  // ----------------------------------------------------------------
  //  Public Template-Bound Properties
  // ----------------------------------------------------------------

  /** Controls whether the sample renders a client-side or server-side grid. */
  protected readonly selectedScenario = signal<GridSampleScenario>("server-endpoint");

  /** Prevents stale grid state by remounting the grid after mode changes. */
  protected readonly showGrid = signal<boolean>(true);

  /** Form values used by the mode selector controls. */
  protected modeFormValues: ModeFormValues = {
    scenario: "server-endpoint",
    loadMode: EzGridLoadMode.Auto,
  };

  /** Input config for selecting client/server and server query mode. */
  protected readonly modeFormConfig: InputConfig[] = [
    {
      type: EzFormInputType.SELECT,
      name: "scenario",
      label: "Scenario",
      width: "2x",
      options: [
        { value: "server-endpoint", viewValue: "Server Legacy | [readEndpoint]" },
        { value: "server-ds-get", viewValue: "Server Legacy | dsConfig + GET" },
        { value: "server-ds-odata", viewValue: "Server OData | dsConfig + GET_ODATA" },
        { value: "client-rowData", viewValue: "Client | [rowData]" },
      ]
    },
    {
      type: EzFormInputType.SELECT,
      name: "loadMode",
      label: "Load Mode",
      width: "1x",
      options: [
        { value: EzGridLoadMode.Auto, viewValue: EzGridLoadMode.Auto },
        { value: EzGridLoadMode.DeferInitial, viewValue: EzGridLoadMode.DeferInitial },
        { value: EzGridLoadMode.RequireFilter, viewValue: EzGridLoadMode.RequireFilter },
      ],
    },
    { type: EzFormInputType.BUTTON, name: "manualReload", buttonText: "Reload", width: "1x" },
  ];

  /** Client-side rowData for the sample grid. */
  protected readonly rowData = signal<StarWarsActor[]>([]);

  /** Simple load counter to make manual reload behavior visible. */
  protected readonly loadCount = signal<number>(0);

  /** Server-side datasource config that switches protocol based on the selected server mode. */
  protected serverDsConfig: EzGridDsConfig = this.createServerDsConfig("GET");

  /** Code sample for the selected scenario. */
  protected readonly modeCodeSample = signal<string>("");

  /** Markdown-fenced code sample for formatted rendering. */
  protected readonly modeCodeMarkdown = computed(() => `\`\`\`html
${this.modeCodeSample()}
\`\`\``);

  /** Shows a note that loadMode does not block rendering when client rowData is already assigned. */
  protected readonly showClientNonAutoLoadModeNote = computed(
    () => this.selectedScenario() === "client-rowData" && this.selectedLoadMode() !== EzGridLoadMode.Auto
  );

  /** Active load mode passed to the rendered grid instance. */
  protected readonly selectedLoadMode = signal<EzGridLoadMode>(EzGridLoadMode.Auto);

  /** Manual reload trigger used by panel-header actions and bound to ez-grid reload input. */
  protected readonly manualReloadTrigger = createTrigger();

  /** Shows the most recent AG Grid context-menu event observed by the sample. */
  protected readonly lastContextMenuEventText = signal<string>("Right-click a cell or header to try the EZ Grid context menu.");

  /** Filter inputs shown in the panel header. */
  protected readonly filterInputConfigs: InputConfigForFilters[] = [
    {
      type: EzFormInputType.TEXT,
      name: "lastName",
      label: "Last Name",
      width: "1x",
      matchType: CrudMatchType.Contains,
    },
    {
      type: EzFormInputType.SELECT,
      name: "trilogy",
      label: "Trilogy",
      width: "1x",
      matchType: CrudMatchType.Equals,
      options: [
        { value: "original", viewValue: "Original" },
        { value: "prequel", viewValue: "Prequel" },
        { value: "sequel", viewValue: "Sequel" },
        { value: "expanded", viewValue: "Expanded" },
      ],
    },
  ];

  /** Header configuration including filters and a manual reload action. */
  protected readonly panelHeaderConfig: PanelHeaderConfig = {
    panelHeaderTitle: "Star Wars Actors",
    subtitle: "",
    showStandardFilterControls: true,
    showReloadButton: true,
    hideExportToExcel: true,
    settingsKey: "Showcase:EzGridLoadDataSample.ContextMenu",
    filterInputConfigs: this.filterInputConfigs,
  };

  /** Demonstrates both built-in items and one context-aware custom item. */
  protected readonly contextMenuConfig: EzGridContextMenuConfigFactory<StarWarsActor> = (currentMenuContext) => ({
    enabled: true,
    builtInItems: [
      "copy",
      "copyRaw",
      "copySelectedText",
      "filterTo",
      "clearFilter",
      "sortAsc",
      "sortDesc",
      "pinLeft",
      "pinRight",
      "unpin",
      "chooseColumns",
      "resetColumns",
      "resetColumn",
    ],
    customItems: [
      {
        id: "sampleCustomItem",
        labelTemplate: "Sample Custom Item for {{rowData.firstName}} {{rowData.lastName}}",
        visible: computed(() => currentMenuContext()?.target === "cell" && !!currentMenuContext()?.rowData),
        command: (context) => {
          const actor = context.rowData as StarWarsActor | undefined;
          this.lastContextMenuEventText.set(
            actor
              ? `Sample custom item clicked for ${actor.firstName} ${actor.lastName} on ${context.field ?? context.columnId ?? "unknown column"}.`
              : "Custom item clicked."
          );
        },
      },
    ],
  });

  /** Explicit column definitions for predictable filter mapping. */
  protected readonly colDefs: ColDef<StarWarsActor>[] = [
     {
          field: 'id',
          maxWidth: 120,
          type: [EzGridColTypes.StarWarsIdButtonColumn],
           cellRendererParams: {
             onButtonClick: (event) => {
               const id = String((event?.rowData as { id?: unknown })?.id ?? "");
               window.alert("Clicked id: " + id);
              },
           } satisfies EzButtonCellParams,
        },
    { field: "firstName", headerName: "First Name" },
    { field: "lastName", headerName: "Last Name", filter: "agTextColumnFilter" },
    { field: "actorId", headerName: "Actor Id", maxWidth: 120, ezContextMenu: { enabled: false } },
    { field: "dateOfBirth", headerName: "Date Of Birth", maxWidth: 140 },
    { field: "notableRole", headerName: "Notable Role", minWidth: 220 },
    { field: "trilogy", headerName: "Trilogy", maxWidth: 120, filter: "agTextColumnFilter" },
    { field: "isLiving", headerName: "Is Living", maxWidth: 120 },
  ];

  // ----------------------------------------------------------------
  //  Lifecycle Hooks
  // ----------------------------------------------------------------

  ngOnInit(): void {
    this.applyModeValues();
    this.loadActors();
  }

  // ----------------------------------------------------------------
  //  Public Methods
  // ----------------------------------------------------------------

  /** Applies mode changes as soon as a selector value changes. */
  protected onModeFormValuesChange(_: Partial<ModeFormValues>): void {
    this.applyModeValues();
  }

  /** Handles mode form actions. */
  protected onModeFormButtonClick(event: { name: string; event: Event } | ButtonConfig): void {
    const actionName = "name" in event ? event.name : event.id;

    if (actionName === "manualReload") {
      this.triggerReload();
    }
  }

  // ----------------------------------------------------------------
  //  Private Helper Methods
  // ----------------------------------------------------------------

  /** Applies mode form values and remounts the grid to clear previous mode state. */
  private applyModeValues(): void {
    this.selectedScenario.set(this.modeFormValues.scenario);
    this.selectedLoadMode.set(this.modeFormValues.loadMode);
    this.serverDsConfig = this.createServerDsConfig(
      this.modeFormValues.scenario === "server-ds-odata" ? "GET_ODATA" : "GET"
    );
    this.modeCodeSample.set(this.getModeCodeSample(this.modeFormValues.scenario, this.modeFormValues.loadMode));
    this.updatePanelHeaderSubtitle();
    this.showGrid.set(false);
    setTimeout(() => this.showGrid.set(true), 0);
  }

  /** Triggers a reload for the active mode. */
  private triggerReload(): void {
    this.manualReloadTrigger.pulse();
    if (this.selectedScenario() === "client-rowData") {
      this.loadActors();
    }
  }

  /** Loads Star Wars actors and assigns them to rowData (client-side mode). */
  private loadActors(): void {
    this.crudService.GetList("mock/StarWarsActors?pageNumber=1&pageSize=5000").pipe(this.takeUntilDestroyed()).subscribe((response) => {
      const rows = this.crudService.getCrudListApiResponse(response).data ?? [];
      this.rowData.set(rows as StarWarsActor[]);
      this.loadCount.update((count) => count + 1);
    });
  }

  /** Builds server datasource config for the selected protocol. */
  private createServerDsConfig(protocol: CrudApiProtocol): EzGridDsConfig {
    return {
      datasourceId: `star-wars-actors-server-${protocol.toLowerCase()}`,
      bindings: [{ bindingType: "READ", protocol, endpoint: "mock/StarWarsActors" }],
    };
  }

  /** Updates header subtitle text to reflect current sample settings. */
  private updatePanelHeaderSubtitle(): void {
    this.panelHeaderConfig.subtitle = `Settings: ${this.selectedScenario()}, load mode = ${this.selectedLoadMode()}`;
  }

  private getModeCodeSample(scenario: GridSampleScenario, loadMode: EzGridLoadMode): string {
    const loadModeBinding = `[loadMode]="'${loadMode}'"`;

    switch (scenario) {
      case "client-rowData":
        return `<ui-grid
  [rowData]="rows"
  ${loadModeBinding}
></ui-grid>`;
      case "server-endpoint":
        return `<ui-grid
  [readEndpoint]="'mock/StarWarsActors'"
  ${loadModeBinding}
></ui-grid>`;
      case "server-ds-get":
        return `<ui-grid [dsConfig]="{
  bindings: [{ bindingType: 'READ', protocol: 'GET', endpoint: 'mock/StarWarsActors' }]
}"
  ${loadModeBinding}
></ui-grid>`;
      case "server-ds-odata":
        return `<ui-grid [dsConfig]="{
  bindings: [{ bindingType: 'READ', protocol: 'GET_ODATA', endpoint: 'mock/StarWarsActors' }]
}"
  ${loadModeBinding}
></ui-grid>`;
      default:
        return "";
    }
  }
}
